
import { useState } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Edit, Filter, PlusCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import BlogPostList from "@/components/blog-management/BlogPostList";
import BlogPostStats from "@/components/blog-management/BlogPostStats";
import { usePermissions } from "@/hooks/usePermissions";
import { toast } from "sonner";

const BlogManagement = () => {
  const [view, setView] = useState<"all" | "published" | "draft" | "scheduled">("all");
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate both blog stats and posts queries
      await queryClient.invalidateQueries({ queryKey: ["blog-stats"] });
      await queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
      toast.success("Blog data refreshed");
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh blog data");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Check if user has permission to access this page
  if (!hasPermission("view:blog")) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">Access Denied</h2>
          <p className="mt-2 text-muted-foreground">You don't have permission to access this page.</p>
          <Button 
            className="mt-4" 
            onClick={() => navigate("/dashboard")}
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Blog Management</h1>
          <p className="text-muted-foreground">Manage your blog posts and content</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2"
            title="Refresh blog data"
          >
            <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
            <span className="hidden sm:inline">{isRefreshing ? "Refreshing..." : "Refresh"}</span>
          </Button>
          <Button 
            onClick={() => navigate("/dashboard/blog/posts/new")} 
            className="flex items-center gap-2"
            disabled={!hasPermission("manage:blog")}
          >
            <PlusCircle size={16} />
            <span>New Post</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="md:col-span-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Blog Performance</CardTitle>
                <CardDescription>Overview of your blog metrics</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <BlogPostStats />
          </CardContent>
        </Card>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 pb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <Tabs 
              defaultValue="all" 
              value={view} 
              className="w-full" 
              onValueChange={(value) => setView(value as any)}
            >
              <TabsList className="mb-4">
                <TabsTrigger value="all">All Posts</TabsTrigger>
                <TabsTrigger value="published">Published</TabsTrigger>
                <TabsTrigger value="draft">Drafts</TabsTrigger>
                <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
              </TabsList>
              
              <TabsContent value="all" className="mt-0">
                <BlogPostList filter="all" key="all-posts" />
              </TabsContent>
              
              <TabsContent value="published" className="mt-0">
                <BlogPostList filter="published" key="published-posts" />
              </TabsContent>
              
              <TabsContent value="draft" className="mt-0">
                <BlogPostList filter="draft" key="draft-posts" />
              </TabsContent>
              
              <TabsContent value="scheduled" className="mt-0">
                <BlogPostList filter="scheduled" key="scheduled-posts" />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogManagement;
